import React, { memo } from "react";
import { Text, View } from "react-native";
import { StyleSheet, scale } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Shadows } from "@/constants/Shadows";
import { LineChart } from "react-native-gifted-charts";
import { UserFinanceDataResponse } from "@/types/financeData";
import useWindow from "@/hooks/useWindow";

interface SpendingSummaryCardProps {
  financeData: UserFinanceDataResponse | null;
}

// Helper to format currency from cents (display only, no $ sign)
const formatCurrencyDisplay = (cents: number): string => {
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// Chart data point type with optional styling
interface ChartDataPoint {
  value: number;
  hideDataPoint?: boolean;
  dataPointColor?: string;
  dataPointRadius?: number;
}

// Generate chart data from spending (last 30 days)
const generateChartData = (
  financeData: UserFinanceDataResponse | null,
): ChartDataPoint[] | null => {
  const byDay = financeData?.analysis?.netWorth?.byDay;
  if (!byDay?.length) return null;

  // Take only the last 30 days
  const last30Days = byDay.slice(-30);

  return last30Days.map((day, index) => {
    const isLast = index === last30Days.length - 1;
    return {
      value: day.spending / 100,
      // Show data point only on the last item
      hideDataPoint: !isLast,
      dataPointColor: isLast ? "#E2451D" : undefined,
      dataPointRadius: isLast ? 5 : undefined,
    };
  });
};

// Get total spending for last 30 days
const getCurrentSpending = (
  financeData: UserFinanceDataResponse | null,
): number => {
  const byDay = financeData?.analysis?.netWorth?.byDay;
  if (!byDay?.length) return 0;
  // Sum spending from the last 30 days
  const last30Days = byDay.slice(-30);
  return last30Days.reduce((sum, day) => sum + day.spending, 0);
};

// Get total budget
const getTotalBudget = (
  financeData: UserFinanceDataResponse | null,
): number => {
  return financeData?.analysis?.totalBudget || 0;
};

export default memo(function SpendingSummaryCard({
  financeData,
}: SpendingSummaryCardProps) {
  const { isDesktop } = useWindow();
  const chartData = generateChartData(financeData);
  const currentSpending = getCurrentSpending(financeData);
  const totalBudget = getTotalBudget(financeData);
  const remaining = Math.max(0, totalBudget - currentSpending);

  return (
    <View style={[styles.card, { marginRight: isDesktop ? 0 : 16 }]}>
      <View style={styles.content}>
        {/* Mini Chart */}
        {chartData && chartData.length > 0 && (
          <View>
            <LineChart
              data={chartData}
              width={scale(100)}
              height={scale(50)}
              hideAxesAndRules
              hideYAxisText
              hideOrigin
              mostNegativeValue={-1}
              thickness={2}
              color="#E2451D"
              areaChart
              startFillColor="rgba(226, 69, 29, 0.2)"
              endFillColor="rgba(226, 69, 29, 0)"
              startOpacity={0.5}
              endOpacity={0}
              initialSpacing={5}
              endSpacing={10}
              spacing={(scale(100) - scale(10)) / (chartData.length - 1)}
              adjustToWidth
              disableScroll
            />
          </View>
        )}

        {/* Spending Info */}
        <View style={styles.spendingInfo}>
          <Text style={styles.label}>Monthly spending</Text>
          <View style={styles.amountRow}>
            <Text style={styles.amount}>
              ${formatCurrencyDisplay(currentSpending)}
            </Text>
            <Text style={styles.leftLabel}> spent</Text>
          </View>
          <Text style={styles.budgetedLabel}>
            out of ${formatCurrencyDisplay(totalBudget)} budgeted
          </Text>
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 20,
    paddingVertical: 24,
    marginLeft: 16,
    ...Shadows.card,
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 40,
  },
  spendingInfo: {
    gap: 4,
  },
  label: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#A3A3A3",
    lineHeight: 23,
  },
  amountRow: {
    flexDirection: "row",
    alignItems: "baseline",
  },
  amount: {
    fontSize: 28,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    lineHeight: 34,
  },
  leftLabel: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
    lineHeight: 23,
  },
  budgetedLabel: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
    lineHeight: 20,
  },
});
